import { EarningsItemWithEntity } from '@api/api/earnings';
import { addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { TFunction } from 'i18next';

export interface CalendarQuarterInfo {
  quarter: string;
  year: number;
  startDate: Date;
  endDate: Date;
}

/** UTC calendar quarter bounds for a date — not company fiscal periods. */
export const getCalendarQuarterInfo = (
  date: Date = new Date()
): CalendarQuarterInfo => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();

  const quarterIndex = Math.floor(month / 3);
  const quarter = `Q${quarterIndex + 1}`;

  const startMonth = quarterIndex * 3;
  const startDate = new Date(Date.UTC(year, startMonth, 1, 0, 0, 0));

  const endMonth = startMonth + 2;
  const endDate = new Date(Date.UTC(year, endMonth + 1, 0, 23, 59, 59));

  return {
    quarter,
    year,
    startDate,
    endDate,
  };
};

interface WeekBucket {
  label: string;
  count: number;
}

export const groupUpcomingByWeeks = ({
  upcoming,
  today = new Date(),
  t,
}: {
  upcoming: {
    date: string;
    count: number;
  }[];
  today?: Date;
  t: TFunction;
}): WeekBucket[] => {
  const parseDate = (dateString: string) => {
    const [y, m, d] = dateString.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
  };

  const formatDate = (d: Date) => formatInTimeZone(d, 'UTC', 'd MMM');

  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const quarter = Math.floor(month / 3);
  const endMonth = quarter * 3 + 3;
  const endDate = new Date(Date.UTC(year, endMonth, 0, 23, 59, 59));
  const startDate = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      0,
      0,
      0
    )
  );

  const msPerDay = 1000 * 60 * 60 * 24;
  const remainingDays = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / msPerDay
  );

  const includeGroupedBucket = remainingDays > 28;
  const bucketCount = includeGroupedBucket ? 5 : Math.ceil(remainingDays / 7);

  const periods: { start: Date; end: Date }[] = [];

  for (let i = 0; i < bucketCount; i++) {
    const start = addDays(startDate, i * 7);
    let end: Date;

    if (includeGroupedBucket) {
      end = i < 4 ? addDays(start, 6) : endDate;
    } else {
      end = addDays(start, 6);

      if (end > endDate) {
        end = endDate;
      }
    }

    periods.push({ start, end });
  }

  return periods
    .map(({ start, end }, idx) => {
      const label =
        includeGroupedBucket && idx === periods.length - 1
          ? t('widget:themeOverview.tabs.earnings.laterThanDate', {
              date: formatDate(start),
            })
          : `${formatDate(start)} - ${formatDate(end)}`;

      const count = upcoming.reduce((sum, { date, count }) => {
        const t = parseDate(date).getTime();
        return sum + (t >= start.getTime() && t <= end.getTime() ? count : 0);
      }, 0);

      return { label, count };
    })
    .filter(period => period.count > 0);
};

export const parseEarnings = (
  earningsData: Record<string, EarningsItemWithEntity[]>
) => {
  const upcomingEarnings: { date: string; count: number }[] = [];
  let totalUpcomingCount = 0;
  let totalReportedCount = 0;

  for (const [date, items] of Object.entries(earningsData)) {
    let upcoming = 0;
    let reported = 0;

    for (const { reported: isReported } of items) {
      isReported ? reported++ : upcoming++;
    }

    if (upcoming > 0) {
      upcomingEarnings.push({ date, count: upcoming });
      totalUpcomingCount += upcoming;
    }

    totalReportedCount += reported;
  }

  return {
    upcomingEarnings,
    totalUpcomingCount,
    totalReportedCount,
  };
};
